import React, {useState} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import {useNavigate} from "react-router";
import {SeoHead} from "./SeoHead";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {RichEditor} from "../admin/RichEditor";
import {useForInstructorsContent} from "../firebase/useForInstructorsContent";
import {ProseArticle} from "./ProseArticle";

export const ForProfessionals = () => {
    const navigate = useNavigate();
    const [forceRefresh, setForceRefresh] = useState(0);
    const [forInstructorsContent, updateForInstructorsContent] = useForInstructorsContent({forceRefresh});
    const isAdmin = useCheckIfAdmin();
    const [editMode, setEditMode] = useState(false);

    const saveHandler = data => {
        updateForInstructorsContent(data);
        setForceRefresh(fr => fr + 1);
        setEditMode(false);
    };

    return (
        <Box sx={{height: "100%", py: {xs: 2, sm: 3}}}>
            <SeoHead
                title="For fagfolk og instruktører"
                description="Informasjon for fagfolk og helsepersonell om å bli sertifisert kursholder i Kurs i bekymringsmestring."
                path="/get-educated"
            />
            <Container maxWidth="md">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/#for-fagfolk")}
                        sx={{
                            minHeight: 44,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "text.secondary",
                            borderRadius: 2,
                            "&:hover": {color: "primary.main"},
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: 2,
                            },
                        }}
                    >
                        Tilbake til forsiden
                    </Button>

                    {isAdmin && !editMode && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => setEditMode(true)}
                            sx={{
                                minHeight: 44,
                                px: 2.5,
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 2,
                                boxShadow: "none",
                                "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineColor: "primary.main",
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            Rediger
                        </Button>
                    )}
                </Box>

                {isAdmin && editMode && (
                    <Box sx={{mb: 4}}>
                        <Alert
                            severity="info"
                            sx={{
                                mb: 3,
                                borderRadius: 3,
                                border: "1px solid rgba(40, 82, 56, 0.15)",
                                bgcolor: "rgba(243, 247, 244, 0.9)",
                                color: "text.primary",
                                "& .MuiAlert-icon": {color: "primary.main"},
                            }}
                        >
                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                <strong>Redigeringsmodus:</strong> Gjør endringer i teksten for fagfolk.
                                Forhåndsvisningen oppdateres automatisk. Klikk «Lagre» for å publisere.
                            </Typography>
                        </Alert>
                        <RichEditor
                            initialData={forInstructorsContent}
                            onSave={saveHandler}
                            onCancel={() => setEditMode(false)}
                        />
                    </Box>
                )}

                {!editMode && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: {xs: 3, sm: 5, md: 6},
                            borderRadius: 4,
                            bgcolor: "#FFFFFF",
                            border: "1px solid rgba(40, 82, 56, 0.08)",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                            mb: 4,
                        }}
                    >
                        <ProseArticle html={forInstructorsContent} />
                    </Paper>
                )}
            </Container>
        </Box>
    );
};